import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { ApiError as HttpApiError } from "@/lib/errors"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import type { Review, ReviewId } from "../data/review-data"

import { fromWireReview } from "./adapter"
import type { WireReview } from "./wire"

/**
 * Reads a review by id via React Query.
 *
 * Invariants preserved from the earlier custom implementation:
 *   • AbortSignal-aware: React Query cancels the fetch when the id changes
 *     or the component unmounts, preventing a stale response from clobbering
 *     a fresh one during rapid navigation.
 *   • Stale-while-revalidate: the cached view renders instantly while a
 *     background refetch checks freshness. Configure `staleTime` on the
 *     shared QueryClient (see `app/query-client.ts`) to tune the SWR window.
 *   • 404 → `null` data with no error: the caller distinguishes "loading",
 *     "not found", and "loaded" without also branching on error.status.
 *   • WebSocket-driven invalidation: consumers can call
 *     `queryClient.invalidateQueries({ queryKey: qk.reviews.detail(id) })`
 *     when a live frame lands, and every subscriber re-renders.
 */

async function fetchReview(id: ReviewId, signal: AbortSignal): Promise<Review | null> {
  try {
    const wire = await http.get<WireReview>(
      `/api/v1/reviews/${encodeURIComponent(id)}`,
      { signal },
    )
    return fromWireReview(wire)
  } catch (err) {
    if (err instanceof HttpApiError && err.status === 404) return null
    throw err
  }
}

export function useReviewQuery(id: ReviewId | null): UseQueryResult<Review | null> {
  return useQuery({
    queryKey: id != null ? qk.reviews.detail(id) : ["reviews", "detail", "none"],
    queryFn: ({ signal }) => fetchReview(id as ReviewId, signal),
    enabled: id != null,
  })
}
